<script lang="ts">
  import Form from "../components/Form.svelte";
  import ContactDetail from "./ConfirmContactInfoTask/ContactDetail.svelte";
  import ChangeClientPhoneNumber from "./ConfirmContactInfoTask/ChangeClientPhoneNumber.svelte";
  import ChangeClientEmailAddress from "./ConfirmContactInfoTask/ChangeClientEmailAddress.svelte";
  import ChangeClientPostalAddress from "./ConfirmContactInfoTask/ChangeClientPostalAddress.svelte";
  import ChangeClientName from "./ConfirmContactInfoTask/ChangeClientName.svelte";
  import HandPointing from "~icons/ph/hand-pointing";
  import { domId } from "../lib/dom";
  import { parsePhoneNumberFromString } from "libphonenumber-js";

  type Address = {
    street: string;
    city: string;
    postal_code: string;
    country: string;
  };

  export type Name = {
    firstName: string | undefined;
    middleName: string | undefined;
    surname: string | undefined;
    formerSurname: string | undefined;
  }

  let {
    action,
    existingPhone,
    existingEmail,
    existingAddress,
    existingName,
    countryCodes,
    errors
  }: {
    action: string;
    existingPhone: string;
    existingEmail: string;
    existingAddress: Address;
    existingName: Name;
    countryCodes: [string, string][];
    errors: { [key: string]: string } | null;
  } = $props();

  // svelte-ignore state_referenced_locally
  const originalName = existingName;
  // svelte-ignore state_referenced_locally
  const originalPhone = existingPhone;
  // svelte-ignore state_referenced_locally
  const originalEmail = existingEmail;

  let editing = $state(null)
  // svelte-ignore state_referenced_locally
  let name: Name = $state(existingName);
  // svelte-ignore state_referenced_locally
  let phone = $state(existingPhone);
  // svelte-ignore state_referenced_locally
  let email = $state(existingEmail);
  let address: Address = $state();

  let phoneChanged = $derived(phone !== originalPhone);
  let emailChanged = $derived(email !== originalEmail);
  let nameChanged = $derived(name !== originalName);

  let addressChanged = $derived(!!address);
  let anythingChanged = $derived(phoneChanged || emailChanged || addressChanged || nameChanged);

  let phoneInvalid = $derived(errors.phone && !phoneChanged);
  let emailInvalid = $derived(errors.email && !emailChanged);
  let addressInvalid = $derived(errors.address && !addressChanged);
  let nameInvalid = $derived((errors.first_name || errors.middle_name || errors.surname || errors.former_surname)
    && !nameChanged)
  let anythingInvalid = $derived(phoneInvalid || emailInvalid || addressInvalid || nameInvalid);

  let formattedPhone = $derived.by(() => {
    if (!phone) return "";
    return parsePhoneNumberFromString(phone)?.formatInternational() || phone;
  });

  let formattedAddress = $derived.by(() => {
    const addr = address || existingAddress;
    return [addr.street, [addr.city, addr.postal_code].filter(Boolean).join(", ")];
  });

  let formattedName = $derived([name.firstName, name.middleName, name.surname, name.formerSurname && `(${name.formerSurname})`].filter(Boolean).join(" "))

  const edit = (value: string | null) => (() => editing = value);
  const back = edit(null)
  const closeDialogId = domId("close-dialog");
</script>

<div class="ui-rhythm">
  {#if !editing}
    <h2 class="ui-title">Is your info up to date?</h2>
    <p>This lets us send updates to the right place and allows you to log in.</p>
  
    <ContactDetail
      label="Legal Name"
      value={formattedName}
      onclick={edit("name")}
      hasChanged={nameChanged}
      isInvalid={nameInvalid}
    />
    {#if !nameInvalid && !nameChanged}
      <div class="ui-alert ui-alert-info mb-4 !-mt-1.5">
        <HandPointing class="rotate-90" />
        <p>Ensure this isn't a previous name or nickname.</p>
      </div>
    {/if}
  
    <ContactDetail
      label="Phone Number"
      value={formattedPhone}
      onclick={edit("phone")}
      hasChanged={phoneChanged}
      isInvalid={phoneInvalid}
    />
    <ContactDetail
      label="Email Address"
      value={email}
      onclick={edit("email")}
      hasChanged={emailChanged}
      isInvalid={emailInvalid}
    />
    <ContactDetail
      label="Postal Address"
      value={formattedAddress}
      onclick={edit("address")}
      hasChanged={addressChanged}
      isInvalid={addressInvalid}
    />
  {:else if editing === "phone"}
    <ChangeClientPhoneNumber bind:phone {phoneInvalid} {countryCodes} {back} />
  {:else if editing === "email"}
    <ChangeClientEmailAddress bind:email {emailInvalid} {back} />
  {:else if editing === "address"}
    <ChangeClientPostalAddress bind:address {back} />
  {:else if editing === "name"}
    <ChangeClientName bind:name {back} />
  {/if}
</div>
  
<Form method="patch" id="confirm-contact-info-form" {action}>
  {#if phoneChanged}<input type="hidden" name="account[phone]" value={phone}>{/if}
  {#if emailChanged}<input type="hidden" name="account[email]" value={email}>{/if}
  {#if addressChanged}
    <input type="hidden" name="account[address][street]" value={address["street"]}>
    <input type="hidden" name="account[address][city]" value={address["city"]}>
    <input type="hidden" name="account[address][postal_code]" value={address["postal_code"]}>
    <input type="hidden" name="account[address][country]" value={address["country"]}>
  {/if}
  {#if nameChanged}
    <input type="hidden" name="account[first_name]" value={name.firstName}/>
    <input type="hidden" name="account[middle_name]" value={name.middleName}/>
    <input type="hidden" name="account[surname]" value={name.surname}/>
    <input type="hidden" name="account[former_surname]" value={name.formerSurname}/>
  {/if}
</Form>

<form method="dialog" id={closeDialogId}></form>

<style>
  /* This style overwrite can be removed once the task design is applied more globally */
  button:disabled {
    opacity: 50%;
    color: white;
    background-color: oklch(57.1889% 0.225412 259.339943 / 1);
  }
</style>

{#if !editing}
  {#if errors.base}<div class="text-error">{errors.base}</div>{/if}
  <div class="floating-footer">
    <button class="btn btn-ghost" form={closeDialogId}>Cancel</button>
    <button
      class="btn btn-primary"
      type="submit"
      data-viewer-mode
      disabled={anythingInvalid}
      form='confirm-contact-info-form'
    >
      {#if anythingChanged}Save changes{:else}Everything is correct{/if}
    </button>
  </div>
{/if}
